/**
 * ReplayRecorder — captures a Curvle attempt to a video Blob.
 *
 * Plan B – NOPRO viral artifact #1. The single biggest viral lever we
 * have over Wordle: a 3-second cinematic of *your* free kick that you
 * can paste into Twitter / Threads / WhatsApp / Discord and have it
 * play inline. Unlike Wordle's color grid, this can't be replicated by
 * a screenshot — it's the moment, captured.
 *
 * How it works:
 *   1. Spin up an offscreen <canvas> at 720×720 (square plays well in
 *      every social feed). Mount it into a hidden host div so the
 *      browser actually allocates a backing buffer.
 *   2. Instantiate the same PitchRenderer the live game uses, so the
 *      replay is byte-for-byte what the player saw.
 *   3. Use canvas.captureStream(fps) to get a MediaStream.
 *   4. Drive MediaRecorder for the duration of the replay, calling
 *      renderer.draw() once per frame at the recorder's fps.
 *   5. Stop, await ondataavailable, return the Blob.
 *
 * Output format: video/webm (universal modern-browser support; iOS
 * Safari 17+). Falls back to image PNG if MediaRecorder is unavailable.
 *
 * Browser support note: canvas.captureStream + MediaRecorder are
 * supported on Chrome 50+, Firefox 47+, Safari 14.1+, all modern
 * Android browsers. Older Safari (which lots of WC fans on iOS will be
 * on) gets the PNG fallback automatically.
 */
import { PitchRenderer, type SceneInputs } from "./render";
import { simulate } from "./engine";
import type {
  AimInput,
  KeeperReveal,
  PublicPuzzleParams,
  PuzzleParams,
  Vec3,
} from "./types";

/**
 * Promote PublicPuzzleParams to a full PuzzleParams with an inert keeper
 * so simulate() can run. The recorder doesn't need the real keeper secrets
 * — keeperReveal is rendered separately via SceneInputs.keeperReveal — so
 * we plug in a "doesn't move, can't catch" keeper. Mirrors the
 * withInertKeeper helper in components/Pitch.tsx so the trajectory we
 * compute matches what Pitch's local fallback would have produced.
 */
function withInertKeeper(p: PublicPuzzleParams): PuzzleParams {
  return {
    ...p,
    keeper: {
      restPos: p.keeper.restPos,
      diveTo: p.keeper.restPos,
      reactionTime: 999,
      diveDuration: 1,
      reach: 0,
    },
  };
}

/** Recording resolution. Square format is the common-denominator winner
 *  across X/Threads (1:1 fits both feeds), Instagram (square is native),
 *  WhatsApp status (1:1 looks right), and TikTok (auto-crops). */
const REC_W = 720;
const REC_H = 720;

/** Frames per second. 30 keeps file size ~50-150 KB for ~3 s; 60 doubles
 *  that without much perceptual gain on the small canvas. */
const REC_FPS = 30;

/** Max replay duration in seconds. Set just over our longest realistic
 *  ball-flight time (~2.0 s) plus a 0.3 s outcome flourish + 0.2 s pad. */
const MAX_REPLAY_SEC = 2.6;

/** Pad after the trajectory ends so the viewer sees the result frame. */
const POST_OUTCOME_HOLD_SEC = 0.4;

export interface RecorderOptions {
  params: PublicPuzzleParams;
  aim: AimInput;
  keeperReveal: KeeperReveal;
  /** Optional override for the recording resolution. */
  width?: number;
  height?: number;
  /** Optional override for fps. */
  fps?: number;
}

export interface RecorderResult {
  /** webm video blob, or PNG still if video recording is unavailable. */
  blob: Blob;
  mimeType: string;
  durationSec: number;
}

/**
 * True if the current browser can produce a webm video stream from a
 * canvas. Used by the share UI to decide whether to offer "Share video"
 * vs "Share image".
 */
export function canRecordVideo(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof HTMLCanvasElement === "undefined") return false;
  // captureStream is the gating API on canvas
  if (typeof (HTMLCanvasElement.prototype as unknown as { captureStream?: unknown }).captureStream !== "function") {
    return false;
  }
  if (typeof MediaRecorder === "undefined") return false;
  // MediaRecorder.isTypeSupported is the canonical capability check
  return (
    MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ||
    MediaRecorder.isTypeSupported("video/webm;codecs=vp8") ||
    MediaRecorder.isTypeSupported("video/webm") ||
    MediaRecorder.isTypeSupported("video/mp4")
  );
}

/** Pick the best supported MIME for the recorder. */
function pickMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "video/webm";
}

/**
 * Build a minimal CanvasView (the shape PitchRenderer expects) that
 * doesn't depend on a ResizeObserver or DOM measurement — recorder
 * canvas is fixed-size, no responsiveness needed.
 */
function attachOffscreen(canvas: HTMLCanvasElement, w: number, h: number) {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Recorder: canvas 2d context unavailable");
  // Render at 1 DPR — recorder canvas is already at output resolution; we
  // don't need extra retina scaling and it costs file size.
  canvas.width = w;
  canvas.height = h;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return {
    ctx,
    cssWidth: w,
    cssHeight: h,
    dpr: 1,
    dispose: () => undefined,
  };
}

/**
 * Record an attempt to a webm video Blob. Resolves when recording is
 * complete. Rejects on browser incapability or recording error.
 */
export async function recordAttemptVideo(
  opts: RecorderOptions,
): Promise<RecorderResult> {
  if (!canRecordVideo()) {
    throw new Error("Recorder: video capture not supported in this browser");
  }
  const w = opts.width ?? REC_W;
  const h = opts.height ?? REC_H;
  const fps = opts.fps ?? REC_FPS;

  // 1. Off-DOM canvas. Mounting in a hidden host is required on some
  //    browsers (Safari) for captureStream to actually produce frames.
  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  host.appendChild(canvas);
  document.body.appendChild(host);

  let recorder: MediaRecorder | null = null;
  try {
    const view = attachOffscreen(canvas, w, h);
    const renderer = new PitchRenderer(view, opts.params);

    // 2. Compute the trajectory once. simulate() needs full PuzzleParams,
    //    so we synthesize an inert-keeper variant. The renderer animates
    //    the *real* keeper dive separately via SceneInputs.keeperReveal.
    const sim = simulate(opts.aim, withInertKeeper(opts.params));
    const trajectory: Vec3[] = sim.trajectory;
    const trajectoryDuration = Math.min(
      MAX_REPLAY_SEC,
      Math.max(0.4, sim.hitTime || trajectory.length / 60),
    );
    const totalDuration = Math.min(
      MAX_REPLAY_SEC + POST_OUTCOME_HOLD_SEC,
      trajectoryDuration + POST_OUTCOME_HOLD_SEC,
    );

    // 3. Capture stream + recorder
    const stream = (canvas as unknown as {
      captureStream: (frameRate?: number) => MediaStream;
    }).captureStream(fps);

    const mimeType = pickMimeType();
    recorder = new MediaRecorder(stream, {
      mimeType,
      // Modest bitrate — at 720×720, 30fps, ~600 kbps is plenty crisp.
      videoBitsPerSecond: 600_000,
    });

    const chunks: Blob[] = [];
    const recordingDone = new Promise<Blob>((resolve, reject) => {
      recorder!.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder!.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
      recorder!.onerror = (e) => reject((e as ErrorEvent).error ?? e);
    });

    recorder.start(/* timeslice */ 100);

    // 4. Drive the render loop. Step replayTime in equal fps slices
    //    until totalDuration. Render via real wall-clock RAF so the
    //    captureStream sees real frame intervals (Safari is picky about
    //    this — synchronous tight loops produce single-frame videos).
    const startWall = performance.now();
    await new Promise<void>((resolve) => {
      const tick = (now: number): void => {
        const elapsed = (now - startWall) / 1000;
        // Sample ball position from the trajectory at the current time.
        const idx = Math.min(
          trajectory.length - 1,
          Math.floor(Math.min(elapsed, trajectoryDuration) * 60),
        );
        const ball = trajectory[idx] ?? { x: 0, y: 0.11, z: 0 };
        const scene: SceneInputs = {
          ball,
          replayTime: elapsed,
          phase: "REPLAYING",
          aim: null,
          preview: null,
          keeperReveal: opts.keeperReveal,
        };
        renderer.draw(scene);

        if (elapsed >= totalDuration) {
          // One final frame at the end so the result state is captured
          // even if the next RAF would be after stop().
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

    // 5. Stop and await final blob
    recorder.stop();
    const blob = await recordingDone;
    return { blob, mimeType, durationSec: totalDuration };
  } finally {
    // Cleanup. MediaRecorder + stream are GC'd by going out of scope.
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        /* noop */
      }
    }
    document.body.removeChild(host);
  }
}

/**
 * Fallback — render a single PNG frame at the trajectory's apex (or
 * outcome moment). Used when MediaRecorder is unavailable or the
 * caller asks for a still poster image (also useful as the OG card
 * source if we don't have a server-rendered one).
 */
export async function recordAttemptStill(
  opts: RecorderOptions,
): Promise<RecorderResult> {
  const w = opts.width ?? REC_W;
  const h = opts.height ?? REC_H;

  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  host.appendChild(canvas);
  document.body.appendChild(host);

  try {
    const view = attachOffscreen(canvas, w, h);
    const renderer = new PitchRenderer(view, opts.params);
    const sim = simulate(opts.aim, withInertKeeper(opts.params));
    const trajectory = sim.trajectory;

    // Pick the moment of impact — apex for over/wide, contact frame for
    // goal/saved/post/wall — to make the still as cinematic as possible.
    const impactIdx = Math.min(
      trajectory.length - 1,
      Math.floor((sim.hitTime || trajectory.length / 60) * 60),
    );
    const ball = trajectory[impactIdx] ?? { x: 0, y: 0.11, z: 0 };
    const replayTime = impactIdx / 60;

    renderer.draw({
      ball,
      replayTime,
      phase: "REPLAYING",
      aim: null,
      preview: null,
      keeperReveal: opts.keeperReveal,
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png",
      );
    });
    return { blob, mimeType: "image/png", durationSec: 0 };
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * High-level helper. Prefers video; falls back to PNG still on
 * unsupported browsers. The share UI uses this and decides UX based on
 * the returned mimeType.
 */
export async function recordAttempt(
  opts: RecorderOptions,
): Promise<RecorderResult> {
  if (canRecordVideo()) {
    try {
      return await recordAttemptVideo(opts);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[curvle.recorder] video failed, falling back to still:", err);
    }
  }
  return recordAttemptStill(opts);
}
